/* eslint-disable no-empty-pattern */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

let storedUser = null;

try {
  const raw = localStorage.getItem("user");
  storedUser = raw && raw !== "undefined" ? JSON.parse(raw) : null;
} catch (error) {
  storedUser = null;
}

const initialState = {
  user: storedUser,
  loading: false,
  error: null,
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (user, { rejectWithValue }) => {
    try {
      const payload = { ...user, role: 'ADMIN' };
      const response = await axios.post("/api/auth/login", payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/auth/logout", { role: 'ADMIN' });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getUserRoleDetails = createAsyncThunk(
  "auth/getUserRoleDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/getUserRoleDetails`);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const changePassword = createAsyncThunk(
  "profile/changePassword",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/admin/changePassword`, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const email = typeof payload === 'string' ? payload : payload?.email;
      const response = await axios.post(`/api/auth/forgotPassword/sendOtp`, {
        email,
        role: "ADMIN",
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const validateOtp = createAsyncThunk(
  "business/validateOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/api/auth/forgotPassword/validateOtp`,
        { ...payload, role: "ADMIN" },
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const reset = createAsyncThunk(
  "business/reset",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/auth/forgotPassword/reset`, payload);
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const loginUserBeforeOtp = createAsyncThunk(
  "auth/loginUserBeforeOtp",
  async (user, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/auth/loginBeforeOtp", user);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Login failed");
    }
  },
);

export const loginUserAfterOtp = createAsyncThunk(
  "auth/loginUserAfterOtp",
  async (user, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/auth/loginAfterOtp", user);
      return response;
    } catch (error) {
      return rejectWithValue(error || "OTP verification failed");
    }
  },
);

export const loginUserBeforeAuthenticator = createAsyncThunk(
  "auth/loginUserBeforeAuthenticator",
  async (user, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/auth/loginBeforeAuthenticator", user);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Login failed");
    }
  },
);

export const loginUserAfterAuthenticator = createAsyncThunk(
  "auth/loginUserAfterAuthenticator",
  async (user, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/auth/loginAfterAuthenticator", user);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Authenticator verification failed");
    }
  },
);

export const count = createAsyncThunk("auth/count", async (ALL) => {
  const response = await axios.get(`/api/admin/count/${ALL}`);
  return response.response || [];
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  passChangeLoading: false,
  passChangeError: null,
  allCountData: {},

  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.response;
      localStorage.setItem("user", JSON.stringify(action.payload.response));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("admin_session_active", "true");
      }
      state.error = null;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.userDetails = null;
      localStorage.removeItem("user");
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem("admin_session_active");
      }
    });
    builder.addCase(logoutUser.rejected, (state) => {
      // Even if API fails, clear state so UI redirects to login
      state.user = null;
      state.userDetails = null;
      localStorage.removeItem("user");
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem("admin_session_active");
      }
    });

    builder.addCase(getUserRoleDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getUserRoleDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      localStorage.setItem("user", JSON.stringify(action.payload));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("admin_session_active", "true");
      }
    });
    builder.addCase(getUserRoleDetails.rejected, (state, action) => {
      state.loading = false;
      state.userDetails = {};
    });

    builder.addCase(changePassword.pending, (state) => {
      state.passChangeLoading = true;
      state.passChangeError = null;
    });
    builder.addCase(changePassword.fulfilled, (state, action) => {
      state.passChangeLoading = false;
      state.passChangeError = null;
    });
    builder.addCase(changePassword.rejected, (state, action) => {
      state.passChangeLoading = false;
      state.passChangeError = action.payload;
    });

    builder.addCase(sendOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendOtp.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(sendOtp.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(validateOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(validateOtp.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(validateOtp.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(reset.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(reset.fulfilled, (state, action) => {
      state.loading = false;
      state.error = "";
    });
    builder.addCase(reset.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(loginUserBeforeOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserBeforeOtp.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(loginUserBeforeOtp.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(loginUserAfterOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserAfterOtp.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.response;
      localStorage.setItem("user", JSON.stringify(action.payload.response));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("admin_session_active", "true");
      }
      state.error = null;
    });
    builder.addCase(loginUserAfterOtp.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(loginUserBeforeAuthenticator.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserBeforeAuthenticator.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(loginUserBeforeAuthenticator.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(loginUserAfterAuthenticator.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserAfterAuthenticator.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.response;
      localStorage.setItem("user", JSON.stringify(action.payload.response));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("admin_session_active", "true");
      }
      state.error = null;
    });
    builder.addCase(loginUserAfterAuthenticator.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(count.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(count.fulfilled, (state, action) => {
      state.loading = false;
      state.allCountData = action.payload;
    });
    builder.addCase(count.rejected, (state, action) => {
      state.loading = false;
      state.allCountData = {};
      state.error = action.error.message;
    });
  },

  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
  },
});

export const { clearAuthError } = authSlice.actions;

export default authSlice.reducer;
